import { DateTime } from "luxon";

/**
 * UTC formatındaki bir tarihi, verilen timezone'a göre formatlanmış şekilde döner.
 *
 * @param {string | Date} date - UTC tarih (MongoDB'den gelen ISO string veya Date objesi)
 * @param {string} timezone - Kullanıcının timezone bilgisi (örnek: "Europe/Istanbul")
 * @param {string} format - Opsiyonel. Dönüştürülmüş tarih formatı. Varsayılan: "yyyy-MM-dd HH:mm"
 * @returns {string} - Formatlanmış tarih stringi
 */
export function formatDateWithTimezone(date, timezone = "Europe/Istanbul", format = "yyyy-MM-dd HH:mm") {
  if (!date || !timezone) return "";

  try {
    return DateTime.fromISO(new Date(date).toISOString(), { zone: "utc" }).setZone(timezone).toFormat(format);
  } catch (error) {
    console.error("Tarih formatlama hatası:", error);
    return "";
  }
}

/**
 * Track Vehicle kayıtları (TravelLocation.date / TravelAction.date) sunucu (Europe/Istanbul)
 * duvar-saati formatında saklanır: "YYYY/MM/DD HH:mm:ss". TZ bilgisi taşımadıkları için önce
 * Istanbul kabul edilip izleyicinin timezone'una çevrilir. Türk kullanıcı için sonuç değişmez.
 *
 * @param {string} legacyDateStr - "YYYY/MM/DD HH:mm:ss" (Istanbul duvar-saati)
 * @param {string} timezone - İzleyicinin timezone'u (fallback: "Europe/Istanbul")
 * @param {string} format - Luxon çıktı formatı. Varsayılan: sadece saat "HH:mm:ss"
 * @returns {string} - Çevrilmiş saat; hata/parse edilemezse ham saat kısmı
 */
export function formatIstanbulWallClock(legacyDateStr, timezone = "Europe/Istanbul", format = "HH:mm:ss") {
  if (!legacyDateStr || typeof legacyDateStr !== "string") return "";

  const rawTimePart = legacyDateStr.split(" ")[1] || legacyDateStr;
  const tz = timezone || "Europe/Istanbul";

  try {
    const dt = DateTime.fromFormat(legacyDateStr, "yyyy/MM/dd HH:mm:ss", { zone: "Europe/Istanbul" });
    if (!dt.isValid) return rawTimePart;
    return dt.setZone(tz).toFormat(format);
  } catch (error) {
    return rawTimePart;
  }
}

/**
 * Verilen timezone'da "bugün" stringini döner. Travel.day company timezone'una göre
 * yazıldığı için live sayfalarındaki "bugün" karşılaştırmaları da aynı günü kullanmalı.
 *
 * @param {string} timezone - Company timezone (fallback: "Europe/Istanbul")
 * @param {string} format - Luxon çıktı formatı. Varsayılan Travel.day formatı: "yyyy/MM/dd"
 * @returns {string} - Formatlanmış bugün stringi
 */
export function getTodayInTimezone(timezone, format = "yyyy/MM/dd") {
  const dt = DateTime.now().setZone(timezone || "Europe/Istanbul");
  const safeDt = dt.isValid ? dt : DateTime.now().setZone("Europe/Istanbul");
  return safeDt.toFormat(format);
}

export function formatYearMonthDateWithTimezone(date, timezone = "Europe/Istanbul") {
  if (!date || !timezone) return "";

  try {
    return DateTime.fromISO(new Date(date).toISOString(), { zone: "utc" }).setZone(timezone).toFormat("yyyy-MM-dd");
  } catch (error) {
    console.error("Tarih formatlama hatası:", error);
    return "";
  }
}

export function getEmptyMonthlyData() {
  const monthNames = [
    "Ocak",
    "Şubat",
    "Mart",
    "Nisan",
    "Mayıs",
    "Haziran",
    "Temmuz",
    "Ağustos",
    "Eylül",
    "Ekim",
    "Kasım",
    "Aralık",
  ];
  return monthNames.map((name) => ({ name, amount: 0 }));
}

// Returns a sorted list of timezone option items like:
// [{ value: "Europe/Istanbul", label: "(UTC+03:00) Europe/Istanbul" }, ...]
export function getTimezoneOptions() {
  try {
    let zones = [];
    // Prefer modern Intl API if available (broad browser support in modern engines)
    if (typeof Intl !== "undefined" && typeof Intl.supportedValuesOf === "function") {
      try {
        zones = Intl.supportedValuesOf("timeZone") || [];
      } catch (_) {
        zones = [];
      }
    }
    // Fallback to Luxon availableZones if provided by environment (rare in browsers)
    if (!zones?.length && DateTime?.availableZones) {
      try {
        zones = Array.from(DateTime.availableZones);
      } catch (_) {}
    }

    const now = DateTime.utc();
    const toLabel = (zone) => {
      const offset = now.setZone(zone).offset; // minutes
      const sign = offset >= 0 ? "+" : "-";
      const abs = Math.abs(offset);
      const hh = String(Math.trunc(abs / 60)).padStart(2, "0");
      const mm = String(abs % 60).padStart(2, "0");
      return `(UTC${sign}${hh}:${mm}) ${zone}`;
    };

    const options = (zones || [])
      .filter((z) => typeof z === "string" && z.includes("/"))
      .map((z) => ({
        value: z,
        label: toLabel(z),
      }))
      .filter((o) => Number.isFinite(now.setZone(o.value).offset))
      .sort((a, b) => a.label.localeCompare(b.label));

    // Fallback to a minimal curated list if empty
    if (!options.length) {
      const fallback = [
        "Europe/Istanbul",
        "UTC",
        "Europe/London",
        "Europe/Berlin",
        "Asia/Dubai",
        "Asia/Kolkata",
        "America/New_York",
        "America/Chicago",
        "America/Denver",
        "America/Los_Angeles",
      ];
      return fallback.map((z) => ({ value: z, label: toLabel(z) }));
    }

    return options;
  } catch (e) {
    // As a last resort return a small list
    return [
      { value: "Europe/Istanbul", label: "(UTC+03:00) Europe/Istanbul" },
      { value: "UTC", label: "(UTC+00:00) UTC" },
      { value: "Europe/London", label: "(UTC+00:00) Europe/London" },
      { value: "America/New_York", label: "(UTC-05:00) America/New_York" },
      { value: "America/Los_Angeles", label: "(UTC-08:00) America/Los_Angeles" },
    ];
  }
}
